import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Gift, Download, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: Validate email format
const isValidEmail = (email) => email && email.includes('@');

// Helper: Subscribe and get sample
const subscribeForSample = async (email) => {
  const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return response.json();
};

const FreeSample = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const data = await subscribeForSample(email);
      
      // Show success regardless (even if already subscribed, give them the download)
      setIsSubmitted(true);
      toast.success('Check your email for the free puzzles!');
      setEmail('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="free-sample" className="relative bg-gradient-to-br from-gray-900 to-black py-20 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-600 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-6">
              <Gift className="h-5 w-5" />
              <span className="font-semibold">FREE DOWNLOAD</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Get 5 Free <span className="text-orange-500">Word Search</span> Puzzles!
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Try before you buy! Download our free sampler with 5 hand-picked puzzles 
              from our best-selling books. Perfect for testing your puzzle skills!
            </p>

            <ul className="space-y-3 mb-8">
              {['5 unique themed puzzles', 'Large, easy-to-read print', 'Instant PDF download', 'Plus exclusive offers'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-300">
                  <Sparkles className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-14 px-6 text-lg border-2 border-gray-700 bg-gray-800 text-white rounded-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    disabled={isSubmitting}
                    required
                  />
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 h-14 text-lg rounded-full shadow-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        <Download className="mr-2 h-5 w-5" />
                        Get Free Puzzles
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-gray-500">
                  No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            ) : (
              <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-6 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-white mb-2">You're All Set!</h3>
                <p className="text-gray-300">
                  Check your inbox for the download link. Happy puzzling!
                </p>
              </div>
            )}
          </div>

          {/* Right: Image */}
          <div className="relative hidden md:block">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-orange-500/20 rounded-3xl filter blur-2xl transform scale-105"></div>
              
              {/* Sample book mockup */}
              <div className="relative bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-8 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-white rounded-2xl p-6 shadow-inner">
                  <div className="text-center">
                    <p className="text-orange-500 font-bold text-sm mb-2">FREE SAMPLER</p>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">RCJ Puzzle Books</h3>
                    <p className="text-gray-600 text-sm mb-4">5 Word Search Puzzles</p>
                    <div className="grid grid-cols-5 gap-1 mb-4">
                      {Array.from({ length: 25 }).map((_, i) => (
                        <div key={i} className="w-full aspect-square bg-gray-100 rounded text-xs flex items-center justify-center text-gray-400 font-mono">
                          {String.fromCharCode(65 + Math.floor(Math.random() * 26))}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">Sample puzzle preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeSample;
